import { useEffect, useState } from 'react';
import {
  makeLevelLabel,
  translateSubjectLevel,
} from '../../components/CalendarEvent';
import { CreateSubject } from '../../components/RegisterForm/TeacherForm/CreateSubject';
import { UpdateSubject } from '../../components/RegisterForm/TeacherForm/UpdateSubject';
import { CheckboxesTags, Option } from '../../components/Inputs/Checkboxes';
import { FormProvider, useForm } from 'react-hook-form';
import { apiService } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { DeleteForever, Add, Edit } from '@mui/icons-material/';
import { PageWrapper } from '../../components/PageWrapper';
import clsx from 'clsx';
import { SearchTable } from '../../components/Table';
import { Button } from '../../components/Button';
import { Level, SubjectEntity } from '../../services/api.types';
import IconButton from '@mui/material/IconButton';
import { Modal } from '../../components/Modal';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { ObjectWithId } from '../../components/Table/types';

export const SubjectsPage = () => {
  const [options, setOptions] = useState<Option[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [onConfirmDelete, setOnConfirmDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const [subjectsTable, setSubjectsTable] = useState<SubjectEntity[]>([]);

  const defaultSelectedSubjectValues = {
    selectedSubjects: [] as Option[],
  };

  const forms = useForm({
    mode: 'all',
    defaultValues: defaultSelectedSubjectValues,
  });

  const selected = forms.watch('selectedSubjects');
  const handleDelete = async () => {
    setIsLoading(true);
    setOnConfirmDelete(true);
    apiService
      .deleteManySubjects({
        ids: selected.map((subject) => Number(subject.id)),
      })
      .then((res) => {
        forms.reset(defaultSelectedSubjectValues);
        const count = res.count;
        toast.success(
          count > 1
            ? count + ' Matérias excluídas.'
            : 'Matéria excluída: ' + selected[0].label
        );
      })
      .finally(() => {
        fetchSubjects();
        setIsLoading(false);
        setOnConfirmDelete(false);
        setOpenDelete(false);
      });
  };
  const handleSelectRow = (row: ObjectWithId) => {
    const subject = subjectsTable.find((s) => s.id === row.id);
    if (!subject) return;
    const selection = {
      id: row.id,
      label: makeLevelLabel[subject.level](subject.name),
    };
    forms.setValue('selectedSubjects', [selection]);
  };

  const fetchSubjects = async () =>
    apiService.getAllSubjects().then((response) => {
      const levelOrder = Object.values(Level);
      response = response.sort((a, b) => {
        const nameComparison = a.name.localeCompare(b.name);
        if (nameComparison !== 0) return nameComparison;

        const aLevelIndex = levelOrder.indexOf(a.level);
        const bLevelIndex = levelOrder.indexOf(b.level);
        if (aLevelIndex < bLevelIndex) return -1;
        if (aLevelIndex > bLevelIndex) return 1;

        return 0;
      });
      setOptions(
        response.map((subject) => ({
          id: subject.id,
          label: makeLevelLabel[subject.level](subject.name),
        }))
      );
      setSubjectsTable(response);
    });

  useEffect(() => {
    fetchSubjects();
  }, []);

  return (
    <PageWrapper>
      <div
        className={clsx(
          'flex flex-col-reverse justify-end lg:flex-row',
          'items-end lg:items-start lg:justify-between',
          'h-full gap-4'
        )}
      >
        <SearchTable
          rows={
            subjectsTable.length
              ? subjectsTable.map((row) => ({
                  id: row.id,
                  Matéria: row.name,
                  Nível: translateSubjectLevel[row.level],
                  Ações: (
                    <>
                      <IconButton
                        title="Editar Matéria"
                        className="h-min bg-transparent hover:bg-orange-primary hover:brightness-100"
                        type="button"
                        onClick={() => {
                          setOpenUpdate(true);
                          handleSelectRow(row);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        title="Excluir Selecionado(s)"
                        className="h-min bg-transparent hover:bg-error hover:brightness-100"
                        type="button"
                        onClick={() => {
                          setOnConfirmDelete(true);
                          handleSelectRow(row);
                        }}
                      >
                        <DeleteForever />
                      </IconButton>
                    </>
                  ),
                }))
              : []
          }
          getSummary={(row) => row['Matéria']}
          isFetching={isLoading}
          filterColumn="Nível"
          filterLabel="Filtrar por"
          searchPlaceholder="Pesquise aqui"
        />
        <div className="w-full max-w-[350px] flex flex-col gap-4">
          <Button
            circular
            title="Criar nova matéria"
            startIcon={<Add />}
            onClick={() => setOpenCreate(true)}
          >
            Criar nova matéria
          </Button>
          <Button
            className="bg-orange-primary hover:bg-error hover:brightness-15"
            circular
            title="Remover Matéria(s)"
            startIcon={<DeleteForever />}
            onClick={() => {
              forms.reset(defaultSelectedSubjectValues);
              setOpenDelete(true);
            }}
          >
            Remover Matéria(s)
          </Button>

          <Modal open={openDelete} onClose={() => setOpenDelete(false)}>
            <FormProvider {...forms}>
              <form className="gap-4 w-[480px] p-4 justify-end">
                <h4>Remover matérias disponíveis</h4>
                <CheckboxesTags name="selectedSubjects" options={options} />
                <div className="pt-4 flex justify-end gap-4">
                  <Button
                    circular
                    size="small"
                    fullWidth={false}
                    title="Cancelar"
                    onClick={() => {
                      forms.reset(defaultSelectedSubjectValues);
                      setOpenDelete(false);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    circular
                    size="small"
                    title="Excluir Selecionado(s)"
                    className="h-min bg-error hover:bg-error hover:brightness-75"
                    fullWidth={false}
                    type="button"
                    onClick={() => {
                      setOnConfirmDelete(true);
                    }}
                    startIcon={<DeleteForever />}
                  >
                    Excluir
                  </Button>
                </div>
              </form>
            </FormProvider>
          </Modal>
        </div>

        <ConfirmationModal
          open={onConfirmDelete}
          onAccept={handleDelete}
          onDecline={() => {
            setOnConfirmDelete(false);
            setOpenDelete(false);
          }}
          isLoading={isLoading}
          acceptText="Excluir"
          declineText="Cancelar"
        >
          <h3>Excluir matéria(s)</h3>
          <h4>
            Confirma a remoção da(s) matéria(s)? Isso pode afetar as aulas e
            professores já cadastrados.
          </h4>
          <>
            {selected.length ? (
              selected.map((s, index) => (
                <p className="m-0" key={s.id}>
                  {s.label}
                </p>
              ))
            ) : (
              <p>Nenhuma matéria selecionada!</p>
            )}
          </>
        </ConfirmationModal>

        <CreateSubject
          open={openCreate}
          onClose={() => {
            setOpenCreate(false);
          }}
          onCreateSubject={(res) => {
            const newSubjects = res.map((subject) => ({
              id: subject.id,
              label: makeLevelLabel[subject.level](subject.name),
            }));
            setOptions([...options, ...newSubjects]);
            setSubjectsTable([...subjectsTable, ...res]);
          }}
        />
        <UpdateSubject
          open={openUpdate}
          onClose={() => {
            setOpenUpdate(false);
          }}
          onUpdateSubject={(res) => {
            fetchSubjects();
          }}
          subjectToUpdate={selected[0]}
        />
      </div>
    </PageWrapper>
  );
};
